import React, { useEffect, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, formatErr } from "../../lib/api";
import { LEVEL_COLOR, ONSET, PAIN_TYPE, zoneText } from "../../lib/wellness";
import { ReviewBox } from "./ReviewBox";

const localTime = (c) => {
  if (c.tz_offset_min === null || c.tz_offset_min === undefined) return `${c.created_at?.slice(11, 16)} UTC`;
  const d = new Date(new Date(c.created_at).getTime() + c.tz_offset_min * 60000);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")} (hora del jugador)`;
};

const Row = ({ c, reload }) => {
  const [open, setOpen] = useState(false);
  const t = c.today;
  return (
    <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "8px 0", fontSize: 13 }} data-testid={`wh-row-${c.id}`}>
      <button onClick={() => setOpen(!open)} style={{ display: "flex", gap: 10, flexWrap: "wrap", width: "100%", background: "none", border: "none", color: "#fff", cursor: "pointer", textAlign: "left", padding: 0, fontSize: 13 }} data-testid={`wh-toggle-${c.id}`}>
        <b style={{ minWidth: 92 }}>{c.local_date}</b>
        <span style={{ color: "#a3a3a3", minWidth: 50 }}>{localTime(c)}</span>
        <span style={{ color: LEVEL_COLOR[t.level], minWidth: 150 }}>{t.level_label} ({t.score})</span>
        <span>Dolor <b style={{ color: c.pain > 6 ? "#f87171" : "#fff" }}>{c.pain}/10</b></span>
        {c.review?.status === "pending" && <span style={{ color: "#f87171", fontWeight: 700 }}>· Revisión pendiente</span>}
        {c.review?.status === "reviewed" && <span style={{ color: "#4ade80" }}>· Revisado</span>}
      </button>
      {open && (
        <div style={{ marginTop: 8, color: "#d4d4d4", lineHeight: 1.7 }} data-testid={`wh-detail-${c.id}`}>
          <div>Sueño {c.sleep_hours} h · calidad {c.sleep_quality}/5 · estrés {c.stress}/5 · fatiga muscular {c.muscle_fatigue}/5 · fatiga general {c.general_fatigue}/5 · ánimo {c.mood}/5 · disposición {c.readiness_to_train}/5</div>
          {c.pain > 0 && <div>Molestia: {zoneText(c.pain_zones)} · {PAIN_TYPE[c.pain_type]?.[0]} · {ONSET[c.pain_onset]}</div>}
          <div>RPE última sesión: {c.rpe_last_session ?? "— (no entrenó)"}{c.rpe_source === "self_reported" ? " · declarado (entrenó ayer)" : c.rpe_session_date ? ` · sesión del ${c.rpe_session_date}` : ""}</div>
          <div>RPE objetivo mostrado: {t.target_rpe ?? "—"}{t.planned_rpe ? ` (planificado ${t.planned_rpe})` : ""}</div>
          {c.comment && <div>Comentario: «{c.comment}»</div>}
          {c.review?.status === "reviewed" && <div style={{ color: "#a3a3a3" }}>Revisado por {c.review.by_name} {c.review.at?.slice(0, 10)}{c.review.note ? `: ${c.review.note}` : ""}</div>}
          {c.review?.status === "pending" && <ReviewBox c={c} onDone={reload} />}
        </div>
      )}
    </div>
  );
};

export const WellnessHistory = ({ clientId }) => {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get(`/admin/clients/${clientId}/wellness?limit=90`).then((r) => setItems(r.data)).catch((e) => setErr(formatErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId]);
  if (err) return <div className="qv-empty">{err}</div>;
  if (!items) return <div style={{ color: "#a3a3a3" }}>Cargando…</div>;
  if (!items.length) return <div className="qv-empty" data-testid="wh-empty">Todavía no ha rellenado ningún cuestionario diario.</div>;
  const dot = items.length < 8;
  const data = [...items].reverse().map((c) => ({ d: c.local_date.slice(5), Bienestar: c.today.score, "Dolor ×10": c.pain * 10, Sueño: c.sleep_hours * 10 }));
  return (
    <div data-testid="wellness-history">
      <div className="qv-card-app" style={{ padding: 16, marginBottom: 12, height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}><CartesianGrid stroke="#222" /><XAxis dataKey="d" stroke="#737373" fontSize={11} /><YAxis domain={[0, 100]} stroke="#737373" fontSize={11} />
            <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} /><Legend wrapperStyle={{ fontSize: 11 }} />
            <Line dataKey="Bienestar" stroke="#4ade80" dot={dot} /><Line dataKey="Dolor ×10" stroke="#f87171" dot={dot} /><Line dataKey="Sueño" stroke="#60a5fa" dot={dot} name="Sueño (h ×10)" />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="qv-card-app" style={{ padding: 16 }}>
        <div className="qv-overline" style={{ marginBottom: 6 }}>Historial de cuestionarios ({items.length})</div>
        {items.map((c) => <Row key={c.id} c={c} reload={load} />)}
      </div>
    </div>
  );
};
